import AssetRenameDialog from '../../../components/AssetRenameDialog';
import { disableAsset, getAssetSearchTasks, manualTriggerAssetSearch, retryAssetSearchTask } from '../../../api';
import { AssetRenameCategory } from '../../../api/assetRename';
import { Project } from '../../../types';
import { tryParseJSON } from './AnalysisElements';
import AssetCommentModal from './AssetCommentModal';
import AssetShareCreateModal from './AssetShareCreateModal';
import AssetShareHistoryModal from './AssetShareHistoryModal';
import DisableAssetConfirmDialog from './DisableAssetConfirmDialog';
import SearchSelectModal from './SearchSelectModal';

export default function ProjectDetailAuxiliaryModals(props: any) {
  const {
    project,
    projectId,
    t,
    toast,
    disableAssetPending,
    disableAssetStep,
    disableAssetAffectedCount,
    disableAssetAffectedDetails,
    disableAssetLoading,
    setDisableAssetPending,
    setDisableAssetStep,
    setDisableAssetLoading,
    setProject,
    renameTarget,
    setRenameTarget,
    commentTarget,
    setCommentTarget,
    reloadAssetComments,
    loadAnalysis,
    loadProject,
    searchSelectModal,
    searchSelectGroups,
    searchSelectedUrls,
    savingSearchSelection,
    searchLightboxIndex,
    searchSelectImages,
    setSearchSelectModal,
    setSearchSelectedUrls,
    handleConfirmSearchSelect,
    setSearchLightboxIndex,
    setSearchTasks,
    assetShareModal,
    assetShareForm,
    assetShareLoading,
    setAssetShareModal,
    setAssetShareForm,
    handleCreateAssetShare,
    showShareHistory,
    assetShareHistory,
    shareHistoryLoading,
    shareHistoryError,
    selectedShareLink,
    selectedShareComments,
    selectedShareImageMap,
    shareCommentsLoading,
    shareCommentsError,
    closeShareHistory,
    backToShareHistory,
    loadShareComments,
    copyAssetShareLink,
  } = props;

  return (
    <>
      <DisableAssetConfirmDialog
        pending={disableAssetPending}
        step={disableAssetStep}
        affectedCount={disableAssetAffectedCount}
        affectedDetails={disableAssetAffectedDetails}
        loading={disableAssetLoading}
        t={t}
        onCancel={() => { setDisableAssetPending(null); setDisableAssetStep(1); }}
        onConfirm={async () => {
          if (!disableAssetPending || !project) return;
          if (disableAssetStep === 1) {
            setDisableAssetStep(2);
            return;
          }
          setDisableAssetLoading(true);
          try {
            await disableAsset(project.id, disableAssetPending.category, disableAssetPending.name, true);
            const oldDisabled: Array<{ category: number; name: string }> = project.disabledAssets ? tryParseJSON(project.disabledAssets) : [];
            const safeOldDisabled = Array.isArray(oldDisabled) ? oldDisabled : [];
            const newDisabled = [...safeOldDisabled, { category: disableAssetPending.category, name: disableAssetPending.name }];
            setProject({ ...(project as Project), disabledAssets: JSON.stringify(newDisabled) });
            toast.success(t('confirmDialogs.disableAsset.disabledSuccess', { name: disableAssetPending.name, count: disableAssetAffectedCount }));
            setDisableAssetPending(null);
            setDisableAssetStep(1);
          } catch (error: any) {
            toast.error(error.message || t('confirmDialogs.disableAsset.disableFailed'));
          } finally {
            setDisableAssetLoading(false);
          }
        }}
      />

      {renameTarget && projectId && (
        <AssetRenameDialog
          open={!!renameTarget}
          projectId={projectId}
          category={renameTarget.category as AssetRenameCategory}
          oldName={renameTarget.name}
          onClose={() => setRenameTarget(null)}
          onApplied={(impact) => {
            const parts = [t('rename.impactAnalysis', { rows: impact.analysisRows })];
            if (impact.shotAssetRefs > 0) parts.push(t('rename.impactShots', { count: impact.shotAssetRefs }));
            if (impact.shotVersionAssetRefs > 0) parts.push(t('rename.impactVersions', { count: impact.shotVersionAssetRefs }));
            const staleTail = impact.promptsStale > 0 ? t('rename.impactPrompts', { count: impact.promptsStale }) : '';
            toast.success(`${t('rename.renamed')}${parts.join(t('rename.separator'))}${staleTail}`);
            loadAnalysis(projectId);
            loadProject(projectId);
            reloadAssetComments();
          }}
        />
      )}

      {commentTarget && projectId && (
        <AssetCommentModal
          projectId={projectId}
          category={commentTarget.category}
          name={commentTarget.name}
          t={t}
          onClose={() => setCommentTarget(null)}
          onChanged={reloadAssetComments}
        />
      )}

      <SearchSelectModal
        modal={searchSelectModal}
        groups={searchSelectGroups}
        selectedUrls={searchSelectedUrls}
        saving={savingSearchSelection}
        lightboxIndex={searchLightboxIndex}
        images={searchSelectImages}
        t={t}
        onClose={() => setSearchSelectModal(null)}
        onClearSelection={() => setSearchSelectedUrls([])}
        onConfirm={handleConfirmSearchSelect}
        onSelectedUrlsChange={setSearchSelectedUrls}
        onLightboxIndexChange={setSearchLightboxIndex}
        onRetry={async (group) => {
          if (!projectId || !group.id) return;
          try {
            await retryAssetSearchTask(projectId, group.id);
            const tasks = await getAssetSearchTasks(projectId);
            setSearchTasks(tasks || []);
            toast.success(t('searchModal.retrySuccess', { n: group.retryCount + 1 }));
          } catch (error: any) {
            toast.error(error.message || t('searchModal.retryFailed'));
          }
        }}
        onManualSearch={async (group) => {
          if (!projectId || !group.id) return;
          try {
            await manualTriggerAssetSearch(projectId, { taskIds: [group.id] });
            const tasks = await getAssetSearchTasks(projectId);
            setSearchTasks(tasks || []);
          } catch (error: any) {
            toast.error(error.message || t('analysis.searchTriggerFailed'));
          }
        }}
      />

      <AssetShareCreateModal
        open={assetShareModal}
        form={assetShareForm}
        loading={assetShareLoading}
        t={t}
        onClose={() => setAssetShareModal(false)}
        onFormChange={setAssetShareForm}
        onSubmit={handleCreateAssetShare}
      />

      <AssetShareHistoryModal
        open={showShareHistory}
        history={assetShareHistory}
        historyLoading={shareHistoryLoading}
        historyError={shareHistoryError}
        selectedLink={selectedShareLink}
        comments={selectedShareComments}
        commentsLoading={shareCommentsLoading}
        commentsError={shareCommentsError}
        imageMap={selectedShareImageMap}
        t={t}
        onClose={closeShareHistory}
        onBack={backToShareHistory}
        onLoadComments={loadShareComments}
        onCopyLink={copyAssetShareLink}
      />
    </>
  );
}
